import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, Check, X as XIcon, Gift, Calendar } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GutscheinEinloesen() {
  const params = useParams();
  const navigate = useNavigate();
  const [nummer, setNummer] = useState(params.nummer || "");
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doLookup = async (n) => {
    const cleaned = n.trim().toUpperCase();
    if (!cleaned) return;
    setLoading(true);
    setError("");
    setVoucher(null);
    try {
      const res = await axios.get(`${API}/vouchers/lookup/${encodeURIComponent(cleaned)}`);
      setVoucher(res.data);
      navigate(`/einloesen/${cleaned}`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Gutscheinnummer nicht gefunden";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.nummer) doLookup(params.nummer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.nummer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doLookup(nummer);
  };

  return (
    <div className="min-h-screen bg-[#F5EFE3] pt-16 pb-24 px-6" data-testid="einloesen-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 border border-[#D68A64] rounded-full text-[#D68A64] text-xs tracking-[0.25em] uppercase">
            <Gift size={14} /> Gutschein einlösen
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#2C2A26] mb-3">
            Ihr Guthaben.
          </h1>
          <p className="text-[#5C574E] font-light max-w-lg mx-auto">
            Geben Sie die Gutscheinnummer aus Ihrem Brief ein — oder scannen Sie
            einfach den QR-Code auf dem Vordruck.
          </p>
        </div>

        {/* Suche */}
        <form onSubmit={handleSubmit} className="mb-10">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              value={nummer}
              onChange={(e) => setNummer(e.target.value)}
              placeholder="GUT-2610-0001"
              className="flex-1 bg-white border border-[#E3DECE] px-5 py-3 text-lg font-mono text-[#2C2A26] focus:outline-none focus:border-[#D68A64]"
              autoFocus
              data-testid="einloesen-nummer-input"
            />
            <button
              type="submit"
              disabled={loading || !nummer.trim()}
              className="inline-flex items-center justify-center gap-2 bg-[#D68A64] hover:bg-[#C07B58] disabled:opacity-40 text-white px-8 py-3 text-sm tracking-[0.2em] uppercase"
              data-testid="einloesen-submit"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
              {loading ? "Suche…" : "Prüfen"}
            </button>
          </div>
        </form>

        {/* Fehler */}
        {error && (
          <div className="bg-red-50 border border-red-200 p-5 mb-8" data-testid="einloesen-error">
            <div className="flex items-start gap-3">
              <XIcon className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-red-900 font-medium">{error}</p>
                <p className="text-sm text-red-700 mt-1">
                  Bitte prüfen Sie die Nummer auf Ihrem Gutschein. Format: GUT-JJMM-NNNN
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Ergebnis */}
        {voucher && (
          <div className="space-y-6" data-testid="einloesen-result">
            {/* Guthaben-Karte */}
            <div className="bg-[#FBF7EC] border-2 border-[#D68A64] p-8">
              <div className="text-xs tracking-[0.25em] uppercase text-[#8A7A68] mb-1">Gutschein</div>
              <div className="font-mono text-xl text-[#BA6D52] font-bold mb-4">
                {voucher.order_number}
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[#8A7A68] mb-1">Guthaben ursprünglich</div>
                  <div className="font-serif text-2xl text-[#5C574E]">
                    {Number(voucher.amount_eur).toFixed(2)} €
                  </div>
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[#8A7A68] mb-1">Aktueller Restwert</div>
                  <div className={`font-serif text-4xl ${voucher.remaining_value_eur > 0 ? "text-[#D68A64]" : "text-[#A8A399]"}`} data-testid="einloesen-remaining">
                    {Number(voucher.remaining_value_eur).toFixed(2)} €
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E3DECE] pt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[#8A7A68]">Produkt:</span>
                  <span className="text-[#2C2A26]">{voucher.product_name}</span>
                </div>
                {voucher.gueltig_bis && (
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-[#8A7A68]" />
                    <span className="text-[#8A7A68]">Gültig bis:</span>
                    <span className={voucher.is_expired ? "text-red-600 font-medium" : "text-[#2C2A26]"}>
                      {voucher.gueltig_bis}
                    </span>
                  </div>
                )}
              </div>

              {/* Status-Badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                {!voucher.is_paid && (
                  <span className="inline-block px-3 py-1 text-xs bg-amber-100 text-amber-800 border border-amber-300">
                    Zahlung noch nicht eingegangen
                  </span>
                )}
                {voucher.is_expired && (
                  <span className="inline-block px-3 py-1 text-xs bg-red-100 text-red-800 border border-red-300">
                    Gutschein abgelaufen
                  </span>
                )}
                {voucher.is_redeemed && (
                  <span className="inline-block px-3 py-1 text-xs bg-emerald-100 text-emerald-800 border border-emerald-300">
                    <Check size={12} className="inline mr-1" /> Vollständig eingelöst
                  </span>
                )}
                {voucher.redemption_count > 0 && !voucher.is_redeemed && (
                  <span className="inline-block px-3 py-1 text-xs bg-blue-100 text-blue-800 border border-blue-300">
                    Teilweise eingelöst ({voucher.redemption_count}×)
                  </span>
                )}
              </div>
            </div>

            {/* Aktion */}
            {voucher.is_paid && !voucher.is_expired && voucher.remaining_value_eur > 0 && (
              <div className="bg-white border border-[#E3DECE] p-8 text-center">
                <h3 className="font-serif text-2xl text-[#2C2A26] mb-3">
                  Termin anfragen
                </h3>
                <p className="text-sm text-[#5C574E] mb-6 leading-relaxed">
                  Ihr Guthaben wird bei Terminabsprache mit Ihrer Menüwunsch-Kalkulation
                  verrechnet. Reicht das Guthaben nicht aus, wird der Restbetrag beim
                  Einsatz fällig. Ist es höher, bleibt der Rest bis zum Ablauf verfügbar.
                </p>
                <Link
                  to={`/anfrage-privat?gutschein=${encodeURIComponent(voucher.order_number)}`}
                  className="inline-flex items-center gap-2 bg-[#D68A64] hover:bg-[#C07B58] text-white px-8 py-3 text-sm tracking-[0.2em] uppercase"
                  data-testid="einloesen-anfrage-btn"
                >
                  Menü-Abend anfragen
                </Link>
              </div>
            )}

            {/* Historie */}
            {voucher.redemption_history?.length > 0 && (
              <div className="bg-white border border-[#E3DECE] p-6">
                <h4 className="text-xs tracking-[0.25em] uppercase text-[#8A7A68] mb-4">
                  Bisherige Einlösungen
                </h4>
                <div className="space-y-2 text-sm">
                  {voucher.redemption_history.map((r, i) => (
                    <div key={i} className="flex justify-between border-b border-[#F0EBDC] pb-2 last:border-0">
                      <span className="text-[#5C574E]">
                        {new Date(r.redeemed_at).toLocaleDateString("de-DE")}
                      </span>
                      <span className="font-serif text-[#2C2A26]">
                        − {Number(r.amount_eur).toFixed(2)} €
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Kontakt */}
        <div className="text-center mt-16 pt-8 border-t border-[#E3DECE] text-sm text-[#8A7A68]">
          <p className="mb-2">Fragen? Ich helfe gern persönlich.</p>
          <a
            href="mailto:mietkoch@stutz-cuisine.com"
            className="text-[#D68A64] hover:underline"
          >
            mietkoch@stutz-cuisine.com
          </a>
          <span className="mx-2">·</span>
          <a href="tel:+4915127282560" className="text-[#D68A64] hover:underline">
            +49 151 27282560
          </a>
        </div>
      </div>
    </div>
  );
}
